import { useRef, useState } from "react";
import { Button, Spinner } from "reactstrap";
import { toast } from "react-toastify";
import { useMutationRequestQuery } from "../../../../Hooks/useMutationRequestQuery";
import { useSafeNavigate } from "../../../../Hooks/useSafeNavigate";
import {
    createOperatorMemberService,
    uploadOperatorCompanyLogoService,
    uploadOperatorCompanyPhotoService,
} from "../../../../Services/CRMAdminServices/CRMOperator";
import { checkMemberImage, MEMBER_IMAGE_ACCEPT } from "./memberImage";
import { formatAbn, ABN_INPUT_MAX_LEN } from "../../../../Utils/companyNumber";


// --- Email / mobile validation ------------------------------------------
// Same rules as the member platform's CompanySettings.jsx, kept in sync so
// the Operator's "create member" form and the member's own settings page
// behave the same way for these fields.
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
// Allowed while typing in an email field: letters, digits, and the
// characters valid in email addresses (@ . _ - +). Blocks other characters.
const EMAIL_TYPING_REGEX = /^[A-Za-z0-9@._+-]*$/;
// Same limit as CompanySettings.jsx's EMAIL_MAX_LEN, kept in sync.
const EMAIL_MAX_LEN = 40;

// Same limit as CompanySettings.jsx's BOOKING_LINK_MAX_LEN, kept in sync.
const BOOKING_LINK_MAX_LEN = 200;

// Matches the auto-formatted output of formatAuMobile(): "0412 345 678"
// - 10 digits grouped 4-3-3, single spaces only. No specific prefix required.
const MOBILE_REGEX = /^\d{4} \d{3} \d{3}$/;
const MOBILE_MAX_LEN = 12;

// Same rules as CompanySettings.jsx's BUSINESS_NAME_REGEX / CONTACT_NAME_REGEX.
const BUSINESS_NAME_REGEX = /^[A-Za-z0-9 &.,'\-]{2,100}$/;
const CONTACT_NAME_REGEX = /^[A-Za-z .,'\-]{2,60}$/;
const CONTACT_NAME_MAX_LEN = 60;

// Same predefined swatches as CompanySettings.jsx — kept identical so a
// company's brand colours look the same whether set by the Operator here
// at account creation, or later by the Owner in their own settings.
const COLOR_OPTIONS = ["#1B4FE0", "#0E2570", "#16803b", "#F5B301", "#b0392e", "#6b21a8"];

// Formats digits into Australian mobile grouping as the user types:
//   0412 345 678  (4-3-3)
function formatAuMobile(raw: string) {
    const digits = raw.replace(/\D/g, "").slice(0, 10); // 04XX XXX XXX
    let out = digits.slice(0, 4);
    if (digits.length > 4) out += " " + digits.slice(4, 7);
    if (digits.length > 7) out += " " + digits.slice(7, 10);
    return out;
}

// Only digits are allowed while typing in a mobile field - spacing is
// handled entirely by formatAuMobile().
function blockNonPhoneKeys(e: React.KeyboardEvent<HTMLInputElement>) {
    const allowed = /[0-9]/;
    const controlKeys = ["Backspace", "Delete", "ArrowLeft", "ArrowRight", "Tab", "Home", "End"];
    if (controlKeys.includes(e.key)) return;
    if (!allowed.test(e.key)) {
        e.preventDefault();
    }
}

// --- ACN validation -------------------------------------------------------
// Australian Company Number: exactly 9 digits, displayed grouped as 3-3-3
// (e.g. "006 164 709"). Optional field.
const ACN_DIGIT_LEN = 9;
const ACN_INPUT_MAX_LEN = 11; // 9 digits + 2 spaces once formatted

function formatAcn(raw: string) {
    const digits = raw.replace(/\D/g, "").slice(0, ACN_DIGIT_LEN);
    return digits.match(/.{1,3}/g)?.join(" ") ?? digits;
}

function validateAcn(value: string) {
    if (!value.trim()) return ""; // optional field
    const digits = value.replace(/\D/g, "");
    if (digits.length !== ACN_DIGIT_LEN) return "ACN must be exactly 9 digits, e.g. 006 164 709.";
    return "";
}

function blockNonAcnKeys(e: React.KeyboardEvent<HTMLInputElement>) {
    const allowed = /[0-9]/;
    const controlKeys = ["Backspace", "Delete", "ArrowLeft", "ArrowRight", "Tab", "Home", "End"];
    if (controlKeys.includes(e.key)) return;
    if (!allowed.test(e.key)) {
        e.preventDefault();
    }
}

// Business name is required — same format rule as CompanySettings.jsx.
function validateBusinessName(value: string) {
    const trimmed = value.trim();
    if (!trimmed) return "Business name is required.";
    if (!BUSINESS_NAME_REGEX.test(trimmed)) return "Use 2-100 characters: letters, numbers, spaces, & . , ' -";
    return "";
}

// Contact name stays optional here (the Operator may not have it on hand
// at account-creation time — the Owner can always fill it in later via
// their own Company Settings), but when it IS filled in, it follows the
// same format rule as CompanySettings.jsx.
function validateContactName(value: string) {
    const trimmed = value.trim();
    if (!trimmed) return "";
    if (trimmed.length > CONTACT_NAME_MAX_LEN) return `Contact name must be ${CONTACT_NAME_MAX_LEN} characters or fewer.`;
    if (!CONTACT_NAME_REGEX.test(trimmed)) return "Letters, spaces and basic punctuation only — no numbers.";
    return "";
}

type FormState = {
    businessName: string;
    shortName: string;
    acn: string;
    abn: string;
    contactName: string;
    contactMobile: string;
    contactEmail: string;
    bookingLink: string;
    brandColorPrimary: string;
    brandColorSecondary: string;
    firstName: string;
    surname: string;
    mobile: string;
    email: string;
    isActive: boolean;
};

const emptyForm: FormState = {
    businessName: "",
    shortName: "",
    acn: "",
    abn: "",
    contactName: "",
    contactMobile: "",
    contactEmail: "",
    bookingLink: "",
    brandColorPrimary: "",
    brandColorSecondary: "",
    firstName: "",
    surname: "",
    mobile: "",
    email: "",
    isActive: true,
};

// contactEmail and email are validated the same way; email is additionally
// required. contactMobile and mobile are validated the same way, both optional.
const EMAIL_FIELDS: (keyof FormState)[] = ["contactEmail", "email"];
const MOBILE_FIELDS: (keyof FormState)[] = ["contactMobile", "mobile"];

const validateSingleField = (field: keyof FormState, value: string, required: boolean) => {
    const trimmed = value.trim();
    if (EMAIL_FIELDS.includes(field)) {
        if (!trimmed) return required ? "Email is required." : "";
        if (trimmed.length > EMAIL_MAX_LEN) return `Max ${EMAIL_MAX_LEN} characters.`;
        if (!EMAIL_REGEX.test(trimmed)) return "Enter a valid email address.";
        return "";
    }
    if (MOBILE_FIELDS.includes(field)) {
        if (!trimmed) return required ? "Mobile is required." : "";
        if (trimmed.length > MOBILE_MAX_LEN) return `Max ${MOBILE_MAX_LEN} characters.`;
        if (!MOBILE_REGEX.test(trimmed)) return "Enter a valid mobile number, e.g. 0412 345 678.";
        return "";
    }
    return "";
};

// bookingLink is optional, same max length AND same "must be a full URL"
// rule as CompanySettings.jsx.
const validateBookingLink = (value: string) => {
    const trimmed = value.trim();
    if (!trimmed) return "";
    if (trimmed.length > BOOKING_LINK_MAX_LEN) return `Max ${BOOKING_LINK_MAX_LEN} characters.`;
    try {
        new URL(trimmed);
        return "";
    } catch {
        return "Enter a full URL, e.g. https://calendly.com/you";
    }
};

const OperatorCreateMember = () => {
    const navigate = useSafeNavigate();
    const [form, setForm] = useState<FormState>(emptyForm);
    const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

    // Logo and contact photo. The account has to exist before a file can be filed
    // against it, so they are held here and uploaded straight after it is created.
    // Refs, not state, are what the upload reads: it runs from the create call's
    // success handler, which must see the files chosen at the moment of submit.
    const logoRef = useRef<File | null>(null);
    const photoRef = useRef<File | null>(null);
    const [logoName, setLogoName] = useState("");
    const [photoName, setPhotoName] = useState("");
    const [isUploadingImages, setIsUploadingImages] = useState(false);

    const chooseImage = (which: "logo" | "photo", file: File | null) => {
        const what = which === "logo" ? "logo" : "contact photo";
        const err = checkMemberImage(file, what);
        if (which === "logo") {
            logoRef.current = err ? null : file;
            setLogoName(err || !file ? "" : file.name);
        } else {
            photoRef.current = err ? null : file;
            setPhotoName(err || !file ? "" : file.name);
        }
        setFieldErrors((prev) => ({ ...prev, [which]: err }));
    };

    // Returns the names of whatever could not be uploaded, so the account is never
    // reported as complete when an image is missing.
    const uploadImages = async (companyId: number | string) => {
        const failed: string[] = [];
        if (logoRef.current) {
            try {
                await uploadOperatorCompanyLogoService(companyId, logoRef.current);
            } catch {
                failed.push("logo");
            }
        }
        if (photoRef.current) {
            try {
                await uploadOperatorCompanyPhotoService(companyId, photoRef.current);
            } catch {
                failed.push("contact photo");
            }
        }
        return failed;
    };

    const handleChange = (field: keyof FormState, rawValue: string | boolean) => {
        if (typeof rawValue === "string" && EMAIL_FIELDS.includes(field) && !EMAIL_TYPING_REGEX.test(rawValue)) {
            return;
        }

        let value: string | boolean = rawValue;
        if (typeof rawValue === "string" && MOBILE_FIELDS.includes(field)) {
            value = formatAuMobile(rawValue);
        } else if (typeof rawValue === "string" && field === "acn") {
            value = formatAcn(rawValue);
        } else if (typeof rawValue === "string" && field === "abn") {
            value = formatAbn(rawValue);
        }

        setForm((prev) => ({ ...prev, [field]: value }));

        if (typeof value === "string" && field === "acn") {
            const acnValue = value;
            setFieldErrors((prev) => ({ ...prev, acn: validateAcn(acnValue) }));
            return;
        }

        if (typeof value === "string" && field === "businessName") {
            const stringValue = value;
            setFieldErrors((prev) => ({ ...prev, businessName: validateBusinessName(stringValue) }));
            return;
        }

        if (typeof value === "string" && field === "contactName") {
            const stringValue = value;
            setFieldErrors((prev) => ({ ...prev, contactName: validateContactName(stringValue) }));
            return;
        }

        if (typeof value === "string" && (EMAIL_FIELDS.includes(field) || MOBILE_FIELDS.includes(field))) {
            const stringValue = value;
            const required = field === "email";
            const err = validateSingleField(field, stringValue, required);
            setFieldErrors((prev) => ({ ...prev, [field]: err }));
            return;
        }

        if (typeof value === "string" && field === "bookingLink") {
            const stringValue = value;
            setFieldErrors((prev) => ({ ...prev, bookingLink: validateBookingLink(stringValue) }));
            return;
        }

        if (fieldErrors[field]) {
            setFieldErrors((prev) => {
                const next = { ...prev };
                delete next[field];
                return next;
            });
        }
    };

    const { mutate: createMember, isPending } = useMutationRequestQuery(
        "createOperatorMember",
        createOperatorMemberService,
        {
            onSuccess: async (res: any) => {
                const body = res?.data;

                setIsUploadingImages(true);
                const failedImages = await uploadImages(body.companyId);
                setIsUploadingImages(false);
                if (failedImages.length > 0) {
                    toast.warn(
                        `Account created, but the ${failedImages.join(" and ")} could not be uploaded. Add it with "Edit details" on the next screen.`
                    );
                }

                if (body?.inviteEmailSent === false) {
                    toast.warn(
                        body?.message ||
                            "Account created, but the invite email failed to send. Ask the owner to use Forgot password on the login screen."
                    );
                } else {
                    toast.success(body?.message || "Member account created.");
                }
                navigate(`/OperatorMemberDetail/${body.companyId}`);
            },
            onError: (err: any) => {
                const status = err?.response?.status;
                const message = err?.response?.data?.message || "Failed to create member account.";
                if (status === 409) {
                    if (/email/i.test(message)) {
                        setFieldErrors({ email: message });
                    } else if (/business name/i.test(message)) {
                        setFieldErrors({ businessName: message });
                    } else {
                        toast.error(message);
                    }
                } else if (status === 400) {
                    toast.error(message);
                } else {
                    toast.error(message);
                }
            },
        }
    );

    const validate = () => {
        const errors: Record<string, string> = {};

        const businessNameErr = validateBusinessName(form.businessName);
        if (businessNameErr) errors.businessName = businessNameErr;

        if (!form.firstName.trim()) errors.firstName = "First name is required.";
        if (!form.surname.trim()) errors.surname = "Surname is required.";

        const acnErr = validateAcn(form.acn);
        if (acnErr) errors.acn = acnErr;

        const contactNameErr = validateContactName(form.contactName);
        if (contactNameErr) errors.contactName = contactNameErr;

        const emailErr = validateSingleField("email", form.email, true);
        if (emailErr) errors.email = emailErr;

        const contactEmailErr = validateSingleField("contactEmail", form.contactEmail, false);
        if (contactEmailErr) errors.contactEmail = contactEmailErr;

        const mobileErr = validateSingleField("mobile", form.mobile, false);
        if (mobileErr) errors.mobile = mobileErr;

        const contactMobileErr = validateSingleField("contactMobile", form.contactMobile, false);
        if (contactMobileErr) errors.contactMobile = contactMobileErr;

        const bookingLinkErr = validateBookingLink(form.bookingLink);
        if (bookingLinkErr) errors.bookingLink = bookingLinkErr;

        setFieldErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        if (!validate()) return;

        const payload: any = {
            businessName: form.businessName.trim(),
            // What their own clients see in a text. Sent only when given - the server
            // falls back to the business name, so a blank here is not a blank message.
            ...(form.shortName.trim() ? { shortName: form.shortName.trim() } : {}),
            firstName: form.firstName.trim(),
            surname: form.surname.trim(),
            email: form.email.trim(),
            isActive: form.isActive,
        };
        if (form.acn.trim()) payload.acn = form.acn.trim();
        if (form.contactName.trim()) payload.contactName = form.contactName.trim();
        if (form.abn.trim()) payload.abn = form.abn.trim();
        if (form.contactMobile.trim()) payload.contactMobile = form.contactMobile.trim();
        if (form.contactEmail.trim()) payload.contactEmail = form.contactEmail.trim();
        if (form.bookingLink.trim()) payload.bookingLink = form.bookingLink.trim();
        if (form.brandColorPrimary.trim()) payload.brandColorPrimary = form.brandColorPrimary.trim();
        if (form.brandColorSecondary.trim()) payload.brandColorSecondary = form.brandColorSecondary.trim();
        if (form.mobile.trim()) payload.mobile = form.mobile.trim();

        createMember(payload);
    };

    return (
        <div className="px-4 pt-4">
            <div
                className="p-4"
                style={{ borderRadius: "8px", boxShadow: "0 4px 8px rgba(0,0,0,0.1)", backgroundColor: "#fff" }}
            >
                <h3 className="f-w-900 heading-color mb-4">Create member</h3>

                <form onSubmit={handleSubmit}>
                    <h6 className="f-w-700 mb-3">Company</h6>
                    <div className="row mb-3">
                        <div className="col-md-6">
                            <label>Business name *</label>
                            <input
                                className={`form-control ${fieldErrors.businessName ? "is-invalid" : ""}`}
                                value={form.businessName}
                                onChange={(e) => handleChange("businessName", e.target.value)}
                            />
                            {fieldErrors.businessName && (
                                <div className="invalid-feedback d-block">{fieldErrors.businessName}</div>
                            )}
                        </div>
                        <div className="col-md-6">
                            <label>Short name</label>
                            <input
                                className="form-control"
                                maxLength={40}
                                value={form.shortName}
                                placeholder={form.businessName.replace(/\s+(Pty\.?\s*)?(Ltd|Limited)\.?$/i, "")}
                                onChange={(e) => handleChange("shortName", e.target.value)}
                            />
                            <small className="text-muted">
                                What their clients see in a text &mdash; "Truevest", not "Truevest Pty Ltd".
                                A text is charged by the character. Blank uses the business name.
                            </small>
                        </div>
                    </div>
                    <div className="row mb-3">
                        <div className="col-md-6">
                            <label>ACN</label>
                            <input
                                className={`form-control ${fieldErrors.acn ? "is-invalid" : ""}`}
                                value={form.acn}
                                onChange={(e) => handleChange("acn", e.target.value)}
                                onKeyDown={blockNonAcnKeys}
                                maxLength={ACN_INPUT_MAX_LEN}
                                placeholder="e.g. 006 164 709"
                            />
                            {fieldErrors.acn && <div className="invalid-feedback d-block">{fieldErrors.acn}</div>}
                        </div>
                        <div className="col-md-6">
                            <label>ABN</label>
                            <input
                                className="form-control"
                                value={form.abn}
                                onChange={(e) => handleChange("abn", e.target.value)}
                                maxLength={ABN_INPUT_MAX_LEN}
                                placeholder="e.g. 99 006 164 709"
                            />
                        </div>
                    </div>

                    <div className="row mb-3">
                        <div className="col-md-4">
                            <label>Contact name</label>
                            <input
                                className={`form-control ${fieldErrors.contactName ? "is-invalid" : ""}`}
                                value={form.contactName}
                                onChange={(e) => handleChange("contactName", e.target.value)}
                                maxLength={CONTACT_NAME_MAX_LEN}
                            />
                            {fieldErrors.contactName && (
                                <div className="invalid-feedback d-block">{fieldErrors.contactName}</div>
                            )}
                        </div>
                        <div className="col-md-4">
                            <label>Contact mobile</label>
                            <input
                                className={`form-control ${fieldErrors.contactMobile ? "is-invalid" : ""}`}
                                value={form.contactMobile}
                                onChange={(e) => handleChange("contactMobile", e.target.value)}
                                onKeyDown={blockNonPhoneKeys}
                                maxLength={MOBILE_MAX_LEN}
                                placeholder="e.g. 0412 345 678"
                            />
                            {fieldErrors.contactMobile && (
                                <div className="invalid-feedback d-block">{fieldErrors.contactMobile}</div>
                            )}
                        </div>
                        <div className="col-md-4">
                            <label>Contact email</label>
                            <input
                                type="email"
                                className={`form-control ${fieldErrors.contactEmail ? "is-invalid" : ""}`}
                                value={form.contactEmail}
                                onChange={(e) => handleChange("contactEmail", e.target.value)}
                                maxLength={EMAIL_MAX_LEN}
                            />
                            {fieldErrors.contactEmail && (
                                <div className="invalid-feedback d-block">{fieldErrors.contactEmail}</div>
                            )}
                        </div>
                    </div>

                    <div className="row mb-3">
                        <div className="col-md-6">
                            <label>Booking link</label>
                            <input
                                className={`form-control ${fieldErrors.bookingLink ? "is-invalid" : ""}`}
                                value={form.bookingLink}
                                onChange={(e) => handleChange("bookingLink", e.target.value)}
                                placeholder="https://cal.com/..."
                                maxLength={BOOKING_LINK_MAX_LEN}
                            />
                            {fieldErrors.bookingLink && (
                                <div className="invalid-feedback d-block">{fieldErrors.bookingLink}</div>
                            )}
                        </div>
                    </div>

                    <div className="row mb-3">
                        <div className="col-md-6">
                            <label>Brand colour — Primary</label>
                            <div className="d-flex" style={{ gap: 8 }}>
                                {COLOR_OPTIONS.map((c) => (
                                    <button
                                        key={c}
                                        type="button"
                                        onClick={() => handleChange("brandColorPrimary", c)}
                                        style={{
                                            width: 30,
                                            height: 30,
                                            borderRadius: 8,
                                            background: c,
                                            cursor: "pointer",
                                            border: form.brandColorPrimary === c ? "3px solid #0E2570" : "1px solid #dee2e6",
                                        }}
                                        aria-label={c}
                                    />
                                ))}
                            </div>
                        </div>
                        <div className="col-md-6">
                            <label>Brand colour — Secondary</label>
                            <div className="d-flex" style={{ gap: 8 }}>
                                {COLOR_OPTIONS.map((c) => (
                                    <button
                                        key={c}
                                        type="button"
                                        onClick={() => handleChange("brandColorSecondary", c)}
                                        style={{
                                            width: 30,
                                            height: 30,
                                            borderRadius: 8,
                                            background: c,
                                            cursor: "pointer",
                                            border: form.brandColorSecondary === c ? "3px solid #0E2570" : "1px solid #dee2e6",
                                        }}
                                        aria-label={c}
                                    />
                                ))}
                            </div>
                        </div>
                    </div>

                    <div className="row mb-3">
                        <div className="col-md-6">
                            <label>Logo</label>
                            <input
                                type="file"
                                accept={MEMBER_IMAGE_ACCEPT}
                                className={`form-control ${fieldErrors.logo ? "is-invalid" : ""}`}
                                onChange={(e) => chooseImage("logo", e.target.files?.[0] || null)}
                            />
                            {fieldErrors.logo && <div className="invalid-feedback d-block">{fieldErrors.logo}</div>}
                            <small className="text-muted">
                                {logoName ? `Chosen: ${logoName}` : "PNG or JPG, 2MB or smaller."}
                            </small>
                        </div>
                        <div className="col-md-6">
                            <label>Contact photo</label>
                            <input
                                type="file"
                                accept={MEMBER_IMAGE_ACCEPT}
                                className={`form-control ${fieldErrors.photo ? "is-invalid" : ""}`}
                                onChange={(e) => chooseImage("photo", e.target.files?.[0] || null)}
                            />
                            {fieldErrors.photo && <div className="invalid-feedback d-block">{fieldErrors.photo}</div>}
                            <small className="text-muted">
                                {photoName
                                    ? `Chosen: ${photoName}`
                                    : "Shown to their clients on their Gameplan. PNG or JPG, 2MB or smaller."}
                            </small>
                        </div>
                    </div>

                    <hr />
                    <h6 className="f-w-700 mb-3">Owner login</h6>
                    <div className="row mb-2">
                        <div className="col-md-4">
                            <label>First name *</label>
                            <input
                                className={`form-control ${fieldErrors.firstName ? "is-invalid" : ""}`}
                                value={form.firstName}
                                onChange={(e) => handleChange("firstName", e.target.value)}
                            />
                            {fieldErrors.firstName && <div className="invalid-feedback d-block">{fieldErrors.firstName}</div>}
                        </div>
                        <div className="col-md-4">
                            <label>Surname *</label>
                            <input
                                className={`form-control ${fieldErrors.surname ? "is-invalid" : ""}`}
                                value={form.surname}
                                onChange={(e) => handleChange("surname", e.target.value)}
                            />
                            {fieldErrors.surname && <div className="invalid-feedback d-block">{fieldErrors.surname}</div>}
                        </div>
                        <div className="col-md-4">
                            <label>Mobile</label>
                            <input
                                className={`form-control ${fieldErrors.mobile ? "is-invalid" : ""}`}
                                value={form.mobile}
                                onChange={(e) => handleChange("mobile", e.target.value)}
                                onKeyDown={blockNonPhoneKeys}
                                maxLength={MOBILE_MAX_LEN}
                                placeholder="e.g. 0412 345 678"
                            />
                            {fieldErrors.mobile && <div className="invalid-feedback d-block">{fieldErrors.mobile}</div>}
                        </div>
                    </div>
                    <div className="row mb-2">
                        <div className="col-md-6">
                            <label>Email *</label>
                            <input
                                type="email"
                                className={`form-control ${fieldErrors.email ? "is-invalid" : ""}`}
                                value={form.email}
                                onChange={(e) => handleChange("email", e.target.value)}
                                maxLength={EMAIL_MAX_LEN}
                            />
                            {fieldErrors.email && <div className="invalid-feedback d-block">{fieldErrors.email}</div>}
                        </div>
                        <div className="col-md-6 d-flex align-items-end">
                            <div className="form-check">
                                <input
                                    className="form-check-input"
                                    type="checkbox"
                                    checked={form.isActive}
                                    onChange={(e) => handleChange("isActive", e.target.checked)}
                                    id="isActiveCheck"
                                />
                                <label className="form-check-label" htmlFor="isActiveCheck">
                                    Create account active
                                </label>
                            </div>
                        </div>
                    </div>

                    <small className="text-muted d-block mb-4">
                        There is no password field - the owner will receive an email to set their own password.
                    </small>

                    <div className="d-flex gap-2">
                        <Button color="primary" type="submit" disabled={isPending || isUploadingImages}>
                            {isPending || isUploadingImages ? (
                                <>
                                    <Spinner size="sm" /> Creating...
                                </>
                            ) : (
                                "Create member"
                            )}
                        </Button>
                        <Button color="secondary" outline type="button" onClick={() => navigate("/CRMOperatorMembers")}>
                            Cancel
                        </Button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default OperatorCreateMember;